/* Admin sign-in. Split screen: brand panel (left, hidden on phones) + form (right).
 * Show/hide password, Caps Lock hint, language switcher; every string through t(). After sign-in an
 * admin lands on /admin/matches. The Beta / Discord note follows Settings → Beta notice. */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usePublicConfig } from '../utils/publicConfig';
import LanguageSwitcher from './LanguageSwitcher';
import './login/login.css';

const Ic = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const UserIc = () => <Ic><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Ic>;
const LockIc = () => <Ic><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></Ic>;
const EyeIc = ({ off }) => (
  <Ic>{off
    ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><path d="M1 1l22 22" /><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" /></>
    : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>}
  </Ic>
);
const CheckIc = () => <Ic size={14}><path d="M20 6L9 17l-5-5" /></Ic>;
const DiscordIc = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286z" /></svg>
);

const SteamIc = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658a3.387 3.387 0 0 1 1.912-.59c.064 0 .127.003.19.006l2.861-4.142V8.91a4.528 4.528 0 0 1 4.524-4.524c2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25a2.551 2.551 0 0 0 3.337-1.373 2.537 2.537 0 0 0 .001-1.95 2.53 2.53 0 0 0-1.38-1.381 2.537 2.537 0 0 0-1.887-.025l1.523.63a1.873 1.873 0 0 1-1.435 3.46zm11.415-9.303a3.019 3.019 0 0 0-3.015-3.015 3.015 3.015 0 1 0 3.015 3.015zm-5.273-.005a2.264 2.264 0 1 1 4.531 0 2.266 2.266 0 0 1-4.531 0z" /></svg>
);
const DISCORD_URL = 'https://discord.gg/PdghFH7TU8';

function AdminLogin() {
  const { t } = useTranslation();
  const publicCfg = usePublicConfig(); // Settings → Beta notice switch hides the Beta / Discord box
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [steamBusy, setSteamBusy] = useState(false);
  const [noAccess, setNoAccess] = useState(null); // Steam64 of a Steam account that is not an admin
  const { login, loginWithSteamCode } = useAuth();
  const navigate = useNavigate();

  // Back from Steam (GET /api/auth/steam/callback): ?steam_code=… → sign in; ?steam_error=… → say why
  const steamHandled = useRef(false);
  useEffect(() => {
    if (steamHandled.current) return;
    steamHandled.current = true;
    const q = new URLSearchParams(window.location.search);
    const code = q.get('steam_code'), err = q.get('steam_error');
    if (!code && !err) return;
    window.history.replaceState(null, '', window.location.pathname); // the code is single use - drop it from the address bar
    if (err) {
      if (err === 'not_admin') { setNoAccess(q.get('steam_id') || ''); return; }
      setError({
        expired: t('login.steamExpired', 'Steam sign-in expired. Please try again.'),
        cancelled: t('login.steamCancelled', 'Steam sign-in was cancelled.'),
        not_verified: t('login.steamNotVerified', 'Steam could not confirm your sign-in. Please try again.'),
      }[err] || t('login.steamFailed', 'Steam sign-in failed. Please try again.'));
      return;
    }
    setSteamBusy(true);
    loginWithSteamCode(code).then(r => {
      if (r.success) navigate('/admin/matches');
      else { setError(r.error || t('login.steamFailed', 'Steam sign-in failed. Please try again.')); setSteamBusy(false); }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const steamSignIn = () => { setSteamBusy(true); setError(''); setNoAccess(null); window.location.href = '/api/auth/steam'; };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);

    if (result.success) {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && (user.role === 'admin' || user.role === 'super_admin' || user.role === 'panel_admin')) {
        navigate('/admin/matches'); // default landing page after login
      } else {
        setError(t('login.adminRequired', 'Admin access required'));
        setLoading(false);
      }
    } else {
      setError(result.error || t('login.failed', 'Sign-in failed. Check your username and password.'));
      setLoading(false);
    }
  };

  // Caps Lock: read from any key event in the password field (no event fires while nothing is typed)
  const capsCheck = (e) => { if (typeof e.getModifierState === 'function') setCapsOn(e.getModifierState('CapsLock')); };

  const features = [
    t('login.feature1', 'Live matches, RCON and chat in one place'),
    t('login.feature2', 'Map veto, brackets and seasons'),
    t('login.feature3', 'Player stats, kill feed and demos'),
  ];

  return (
    <div className="lg-page">
      <aside className="lg-brand">
        <div className="lg-brand-grid" />
        <div className="lg-brand-glow" />
        <div className="lg-brand-inner">
          <div className="lg-logo">
            <img src="/logo.png" alt="" />
            <span>Flux <b>Panel</b></span>
          </div>
          <div className="lg-pitch">
            <h2>{t('login.tagline', 'Run your CS2 tournaments from one panel.')}</h2>
            <ul>{features.map(f => <li key={f}><span className="lg-check"><CheckIc /></span>{f}</li>)}</ul>
          </div>
          <div className="lg-brand-foot">© {new Date().getFullYear()} Flux</div>
        </div>
      </aside>

      <main className="lg-main">
        <div className="lg-top">
          <button type="button" className="lg-back" onClick={() => navigate('/')}>← {t('login.backToSite', 'Public site')}</button>
          <LanguageSwitcher direction="down" />
        </div>

        <div className="lg-form-wrap">
          <div className="lg-mobile-logo"><img src="/logo.png" alt="" /> Flux</div>
          <h1 className="lg-title">{t('login.title', 'Welcome back')}</h1>
          <p className="lg-sub">{t('login.subtitle', 'Sign in to the Flux admin panel')}</p>

          <form onSubmit={handleSubmit} className="lg-form">
            <label className="lg-field">
              <span className="lg-label">{t('login.username', 'Username')}</span>
              <span className="lg-input">
                <UserIc />
                <input type="text" value={username} onChange={e => setUsername(e.target.value)} required autoFocus
                  autoComplete="username" autoCapitalize="none" spellCheck={false} placeholder={t('login.usernamePh', 'Your username')} />
              </span>
            </label>

            <label className="lg-field">
              <span className="lg-label">{t('login.password', 'Password')}</span>
              <span className="lg-input">
                <LockIc />
                <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                  autoComplete="current-password" placeholder="••••••••"
                  onKeyDown={capsCheck} onKeyUp={capsCheck} onBlur={() => setCapsOn(false)} />
                <button type="button" className="lg-eye" onClick={() => setShowPw(v => !v)}
                  aria-label={showPw ? t('login.hidePassword', 'Hide password') : t('login.showPassword', 'Show password')}
                  title={showPw ? t('login.hidePassword', 'Hide password') : t('login.showPassword', 'Show password')}>
                  <EyeIc off={showPw} />
                </button>
              </span>
              {capsOn && <span className="lg-caps">⇪ {t('login.capsLock', 'Caps Lock is on')}</span>}
            </label>

            {error && <div className="lg-error" role="alert">{error}</div>}

            <button type="submit" className="lg-submit" disabled={loading || !username || !password}>
              {loading ? <><span className="lg-spin" />{t('login.signingIn', 'Signing in…')}</> : t('login.signIn', 'Sign in')}
            </button>
          </form>

          <div className="lg-or"><span>{t('login.or', 'or')}</span></div>
          <button type="button" className="lg-steam" onClick={steamSignIn} disabled={steamBusy || loading}>
            {steamBusy ? <><span className="lg-spin" />{t('login.steamSigningIn', 'Signing in with Steam…')}</> : <><SteamIc />{t('login.steamSignIn', 'Sign in with Steam')}</>}
          </button>
          {noAccess !== null && (
            <div className="lg-noaccess" role="alert">
              <b>{t('login.steamNoAccessTitle', 'Not allowed')}</b>
              <span>{t('login.steamNoAccess', 'Your Steam account is not an admin on this panel, so you are not allowed to sign in. Ask for access on our Discord.')}</span>
              {noAccess && <span className="lg-noaccess-id">Steam64: <code>{noAccess}</code></span>}
              <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer"><DiscordIc /> {t('login.steamAskDiscord', 'Request access on Discord')}</a>
            </div>
          )}

          {publicCfg && !publicCfg.hide_beta_banner && (
            <div className="lg-beta">
              <span className="lg-beta-tag">BETA</span>
              {t('login.beta', 'Report bugs or request beta admin access on our')}{' '}
              <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer"><DiscordIc /> Discord</a>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminLogin;
